import shpwrite from "@mapbox/shp-write";

/** Trigger a browser file download */
const downloadFile = (content, filename, mimeType, asBinary = false) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** Escape XML special characters for KML */
const escapeXML = (str) =>
  String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

/** Convert properties object to KML ExtendedData block */
const propsToExtendedData = (props = {}) => {
  const entries = Object.entries(props)
    .map(
      ([k, v]) =>
        `      <Data name="${escapeXML(k)}"><value>${escapeXML(v)}</value></Data>`,
    )
    .join("\n");
  return entries ? `    <ExtendedData>\n${entries}\n    </ExtendedData>` : "";
};

// ─── KML ──────────────────────────────────────────────────────────────────────

const CONTOUR_STYLES = {
  input:   { color: "ff0000ff", width: 3 },
  result1: { color: "ff0c0c82", width: 3 },
  result2: { color: "ff1b1bb5", width: 3 },
  result3: { color: "ff0045ff", width: 3 },
  result4: { color: "ff008cff", width: 3 },
  drawn:   { color: "ff00ff00", width: 2 },
  point:   { color: "ff0000ff", width: 1 },
};

const buildKMLStyle = (styleId, cfg) => `
  <Style id="${styleId}">
    <LineStyle><color>${cfg.color}</color><width>${cfg.width}</width></LineStyle>
    <PolyStyle><fill>0</fill></PolyStyle>
    <IconStyle><color>${cfg.color}</color></IconStyle>
  </Style>`;

/**
 * Render one GeoJSON feature as a KML Placemark.
 * Coordinates are always GeoJSON [lng, lat] → KML "lng,lat,0". ✓
 * MultiPolygon is expanded to a <MultiGeometry> of <Polygon>s so each
 * ring keeps its original position.
 */
const featureToKMLPlacemark = (feature, styleId, label) => {
  const geom = feature.geometry;
  if (!geom) return "";
  const props = feature.properties || {};
  const name = escapeXML(label || props.label || props.name || geom.type);
  const extData = propsToExtendedData(props);

  const coordStr = (ring) => ring.map(([x, y]) => `${x},${y},0`).join(" ");

  const polygonKML = (coordinates) => {
    const rings = coordinates
      .map((ring, i) =>
        i === 0
          ? `<outerBoundaryIs><LinearRing><coordinates>${coordStr(ring)}</coordinates></LinearRing></outerBoundaryIs>`
          : `<innerBoundaryIs><LinearRing><coordinates>${coordStr(ring)}</coordinates></LinearRing></innerBoundaryIs>`,
      )
      .join("\n      ");
    return `<Polygon>${rings}</Polygon>`;
  };

  let geometryKML = "";

  switch (geom.type) {
    case "Point": {
      const [lng, lat] = geom.coordinates;
      geometryKML = `<Point><coordinates>${lng},${lat},0</coordinates></Point>`;
      break;
    }
    case "Polygon": {
      geometryKML = polygonKML(geom.coordinates);
      break;
    }
    case "LineString": {
      geometryKML = `<LineString><coordinates>${coordStr(geom.coordinates)}</coordinates></LineString>`;
      break;
    }
    case "MultiPolygon": {
      // Each polygon in the multi-polygon gets its own <Polygon> inside <MultiGeometry>
      const polys = geom.coordinates.map((poly) => polygonKML(poly)).join("\n");
      geometryKML = `<MultiGeometry>${polys}</MultiGeometry>`;
      break;
    }
    case "MultiLineString": {
      const lines = geom.coordinates
        .map((line) => `<LineString><coordinates>${coordStr(line)}</coordinates></LineString>`)
        .join("\n");
      geometryKML = `<MultiGeometry>${lines}</MultiGeometry>`;
      break;
    }
    default:
      return "";
  }

  return `
  <Placemark>
    <name>${name}</name>
    <styleUrl>#${styleId}</styleUrl>
${extData}
    ${geometryKML}
  </Placemark>`;
};

const buildKML = (groups) => {
  const styles = groups
    .map(({ styleId, styleConfig }) => buildKMLStyle(styleId, styleConfig))
    .join("\n");

  const folders = groups
    .filter(({ features }) => features.length > 0)
    .map(({ styleId, label, features }) => {
      const placemarks = features
        .map((f) => featureToKMLPlacemark(f, styleId, label))
        .join("\n");
      return `
  <Folder>
    <name>${escapeXML(label)}</name>
    ${placemarks}
  </Folder>`;
    })
    .join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
<Document>
  <name>Fire Prediction Export</name>
${styles}
${folders}
</Document>
</kml>`;
};

// ─── CSV ──────────────────────────────────────────────────────────────────────

const featuresToCSV = (allGroups) => {
  const rows = [
    ["group", "feature_type", "geometry_type", "longitude", "latitude",
     "ring_index", "coord_index", "properties"],
  ];

  allGroups.forEach(({ label, features }) => {
    features.forEach((f) => {
      const geom = f.geometry;
      if (!geom) return;
      const propsStr = JSON.stringify(f.properties || {}).replace(/"/g, '""');

      if (geom.type === "Point") {
        const [lng, lat] = geom.coordinates;
        rows.push([label, "Point", "Point", lng, lat, 0, 0, `"${propsStr}"`]);
      } else if (geom.type === "Polygon") {
        geom.coordinates.forEach((ring, ri) =>
          ring.forEach(([lng, lat], ci) =>
            rows.push([label, "Polygon", "Polygon", lng, lat, ri, ci, `"${propsStr}"`]),
          ),
        );
      } else if (geom.type === "LineString") {
        geom.coordinates.forEach(([lng, lat], ci) =>
          rows.push([label, "LineString", "LineString", lng, lat, 0, ci, `"${propsStr}"`]),
        );
      } else if (geom.type === "MultiPolygon") {
        geom.coordinates.forEach((poly, pi) =>
          poly.forEach((ring, ri) =>
            ring.forEach(([lng, lat], ci) =>
              rows.push([
                label, `MultiPolygon[${pi}]`, "MultiPolygon",
                lng, lat, ri, ci, `"${propsStr}"`,
              ]),
            ),
          ),
        );
      }
    });
  });

  return rows.map((r) => r.join(",")).join("\n");
};

// ─── SHP ──────────────────────────────────────────────────────────────────────

/**
 * Explode MultiPolygon → array of Polygon features.
 * shp-write does not reliably handle MultiPolygon; splitting fixes positioning.
 */
const explodeMultiPolygons = (features) =>
  features.flatMap((f) => {
    if (f.geometry?.type !== "MultiPolygon") return [f];
    return f.geometry.coordinates.map((polygonCoords, i) => ({
      ...f,
      id: f.id != null ? `${f.id}_${i}` : undefined,
      geometry: { type: "Polygon", coordinates: polygonCoords },
    }));
  });

/**
 * shp-write uses [lat, lng] internally in some builds — we keep GeoJSON
 * [lng, lat] order and rely on the library's own CRS (WGS84 by default).
 * Exploding MultiPolygons is the critical fix for wrong positions.
 */
const exportSHP = async (allFeatures, fileName = "fire_export") => {
  const exploded = explodeMultiPolygons(allFeatures);

  const polygons = exploded.filter((f) => f.geometry?.type === "Polygon");
  const points   = exploded.filter((f) => f.geometry?.type === "Point");
  const lines    = exploded.filter((f) =>
    ["LineString", "MultiLineString"].includes(f.geometry?.type),
  );

  const exportGroup = async (features, typeName) => {
    if (features.length === 0) return;
    const fc = { type: "FeatureCollection", features };
    const options = {
      folder:      `${fileName}_${typeName}`,
      filename:    `${fileName}_${typeName}`,
      outputType:  "blob",
      compression: "DEFLATE",
      types: { point: "points", polygon: "polygons", polyline: "lines" },
    };
    const zipBlob = await shpwrite.zip(fc, options);
    downloadFile(zipBlob, `${fileName}_${typeName}.zip`, "application/zip", true);
  };

  if (polygons.length > 0) await exportGroup(polygons, "polygons");
  if (points.length  > 0) await exportGroup(points,   "points");
  if (lines.length   > 0) await exportGroup(lines,    "lines");
};

// ─── MAIN EXPORT ──────────────────────────────────────────────────────────────

export const handleExportFormat = async (format, params) => {
  const {
    combinedFeatures,
    geoJsonInputContour,
    geoJsonResults,
    drawnPolygons = [],   
    drawnPoints = [],    
    extraGroups = [],    
    fileName = "fire_export",
  } = params;

  const getFeatures = (result) => result?.contour?.features ?? [];

  const predictionGroups = geoJsonResults.map((result, idx) => {
    const hourLabel = idx === 0 ? "t+1h" : `t+${idx + 1}h`;
    const styleKey = idx < 4 ? `result${idx + 1}` : "result4";
    return {
      styleId: `result${idx + 1}`,
      styleConfig: CONTOUR_STYLES[styleKey] || CONTOUR_STYLES.result4,
      label: `Prédiction ${hourLabel}`,
      features: getFeatures(result),
    };
  }).filter(({ features }) => features.length > 0);

  const drawnGroup = drawnPolygons.length > 0
    ? [{
        styleId: "drawn",
        styleConfig: CONTOUR_STYLES.drawn,
        label: "Polygones dessinés",
        features: drawnPolygons,
      }]
    : [];

  const pointGroup = drawnPoints.length > 0
    ? [{
        styleId: "point",
        styleConfig: CONTOUR_STYLES.point,
        label: "Points",
        features: drawnPoints,
      }]
    : [];

  const groups = [
    {
      styleId: "input_contour",
      styleConfig: CONTOUR_STYLES.input,
      label: "Contour initial",
      features: getFeatures(geoJsonInputContour),
    },
    ...predictionGroups,
    ...drawnGroup,
    ...pointGroup,
    ...extraGroups,
  ].filter(({ features }) => features.length > 0);

  if (!combinedFeatures || combinedFeatures.length === 0) {
    console.warn("No features to export.");
    return;
  }
 switch (format) {
    case "geojson": {
      const geojson = { type: "FeatureCollection", features: combinedFeatures };
      downloadFile(
        JSON.stringify(geojson, null, 2),
        `${fileName}.geojson`,
        "application/geo+json",
      );
      break;
    }
    case "kml": {
      const kmlContent = buildKML(groups);
      downloadFile(
        kmlContent,
        `${fileName}.kml`,
        "application/vnd.google-earth.kml+xml",
      );
      break;
    }
    case "csv": {
      const csvContent = featuresToCSV(groups);
      downloadFile(csvContent, `${fileName}.csv`, "text/csv;charset=utf-8;");
      break;
    }
    case "shp": {
      await exportSHP(combinedFeatures, fileName); // see below
      break;
    }
    default:
      console.error("Unknown export format:", format);
  }
};